/**
 * Bank CFO Dashboard Cards — 6 KPIs principaux (spec §11)
 * Backend: GET /api/bank/cfo-kpis/{account_id}?yyyy_mm=
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Landmark, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, Receipt } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fxof = (v) => (v == null ? '—' : `${Number(v).toLocaleString('fr-FR')} XOF`);

const Card = ({ icon: Icon, label, value, sub, color = 'blue', testId }) => (
  <div className={`rounded-lg border border-${color}-200 bg-${color}-50/40 px-3 py-2.5`} data-testid={testId}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className={`h-3.5 w-3.5 text-${color}-600`} />
      <span className="text-[10px] uppercase tracking-wide text-zinc-500 font-semibold">{label}</span>
    </div>
    <div className={`text-lg font-bold text-${color}-900`}>{value}</div>
    {sub && <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>}
  </div>
);

export const BankCFOCards = ({ accountId, yyyyMm, refreshKey = 0 }) => {
  const [kpis, setKpis] = useState(null);
  useEffect(() => {
    if (!accountId) return;
    (async () => {
      try {
        const params = yyyyMm ? { yyyy_mm: yyyyMm } : {};
        const { data } = await axios.get(`${API}/bank/cfo-kpis/${accountId}`, { params });
        setKpis(data);
      } catch (e) { console.error('KPIs fetch failed', e); }
    })();
  }, [accountId, yyyyMm, refreshKey]);

  if (!kpis) return null;

  const gapColor = kpis.balance_gap == null ? 'zinc' : (Math.abs(kpis.balance_gap) > 100 ? 'rose' : 'emerald');
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="bank-cfo-kpi-cards">
      <Card icon={Landmark} label="Trésorerie consolidée" value={fxof(kpis.consolidated_treasury)} color="blue" testId="kpi-treasury" />
      <Card icon={TrendingUp} label="Total crédits" value={fxof(kpis.total_credits)} color="emerald" testId="kpi-credits" />
      <Card icon={TrendingDown} label="Total débits" value={fxof(kpis.total_debits)} color="rose" testId="kpi-debits" />
      <Card icon={CheckCircle2} label="Taux rapprochement" value={`${kpis.reconciled_pct} %`} sub={`${kpis.reconciled} / ${kpis.total_tx} tx`} color="emerald" testId="kpi-recon-pct" />
      <Card icon={AlertTriangle} label="Écart de solde" value={kpis.balance_gap == null ? '—' : fxof(kpis.balance_gap)} color={gapColor} testId="kpi-gap" />
      <Card icon={Receipt} label="Frais bancaires" value={fxof(kpis.bank_fees_total)} sub={`${kpis.unjustified_debits} débits non justifiés`} color="amber" testId="kpi-bank-fees" />
    </div>
  );
};

export default BankCFOCards;
